export interface OwnerIdentity { ownerUserId: string; accessToken: string }
export interface OwnedWorkspace { workspaceId: string; name: string; ownerUserId: string }
export interface ExecutionBinding { customerId: string; workspaceId: string; ownerUserId: string; dataNamespaceId: string; revision: number }
export interface PilotState { workspace: OwnedWorkspace; attachment?: ExecutionBinding }

type Fetcher = (url: string, init: RequestInit) => Promise<Response>;
const objectIdPattern = /^[a-fA-F0-9]{24}$/;
const namespacePattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function record(value: unknown): value is Record<string, unknown> { return value !== null && typeof value === 'object' && !Array.isArray(value); }
function objectId(value: unknown): value is string { return typeof value === 'string' && objectIdPattern.test(value); }
function token(value: unknown): value is string { return typeof value === 'string' && /^[^\s]{1,16384}$/.test(value); }

export function ownerIdentityFromState(value: unknown): OwnerIdentity | undefined {
  if (!record(value) || !record(value.UserReducer)) return undefined;
  const ownerUserId = value.UserReducer.userId, accessToken = value.UserReducer.accessToken;
  return objectId(ownerUserId) && token(accessToken) ? { ownerUserId, accessToken } : undefined;
}

export function ownerUserIdFromState(value: unknown): string | undefined {
  if (!record(value) || !record(value.UserReducer)) return undefined;
  const ownerUserId = value.UserReducer.userId;
  return objectId(ownerUserId) ? ownerUserId : undefined;
}

/** Display-only identity; never used to authorize workspace operations. */
export function ownerEmailFromState(value: unknown): string | undefined {
  if (!record(value) || !record(value.UserReducer)) return undefined;
  const ownerUserId = ownerUserIdFromState(value), user = value.UserReducer.user;
  if (!ownerUserId || !record(user) || user._id !== ownerUserId) return undefined;
  const email = user.email;
  return typeof email === 'string' && email.length <= 254 && /^[^\s<>@]+@[^\s<>@]+$/.test(email) ? email : undefined;
}

async function boundedJson(response: Response): Promise<unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('invalid response');
  const chunks: Uint8Array[] = []; let total = 0;
  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      total += chunk.value.byteLength;
      if (total > 16384) throw new Error('response too large');
      chunks.push(chunk.value);
    }
  } finally { await reader.cancel(); }
  const bytes = new Uint8Array(total); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
  return parsed;
}

function workspaceDto(value: unknown, expectedWorkspace: string, expectedOwner: string): OwnedWorkspace {
  if (!record(value) || value._id !== expectedWorkspace || value.owner !== expectedOwner || typeof value.name !== 'string' || value.name.length < 1 || value.name.length > 200) throw new Error('Pilot setup unavailable');
  return { workspaceId: expectedWorkspace, name: value.name, ownerUserId: expectedOwner };
}

function customerIdFrom(value: unknown, workspaceId: string, ownerUserId: string): string {
  if (!record(value) || !objectId(value._id) || value.workspaceId !== workspaceId || value.ownerUserId !== ownerUserId || value.lifecycleState !== 'active') throw new Error('Pilot setup unavailable');
  return value._id;
}

function bindingDto(value: unknown, customerId: string, workspaceId: string, ownerUserId: string): ExecutionBinding {
  if (!record(value) || Object.keys(value).sort().join(',') !== 'customerId,dataNamespaceId,ownerUserId,revision,workspaceId' ||
      value.customerId !== customerId || value.workspaceId !== workspaceId || value.ownerUserId !== ownerUserId ||
      typeof value.dataNamespaceId !== 'string' || !namespacePattern.test(value.dataNamespaceId) ||
      typeof value.revision !== 'number' || !Number.isSafeInteger(value.revision) || value.revision < 1) throw new Error('Pilot setup unavailable');
  return { customerId, workspaceId, ownerUserId, dataNamespaceId: value.dataNamespaceId, revision: value.revision };
}

export function createAssistantPilotSetup(config: { apiBaseUrl: string; workspaceId: string; ownerUserId: string; accessToken: string; search?: string; hash?: string; fetcher?: Fetcher; timeoutMs?: number }) {
  const timeoutMs = config.timeoutMs ?? 10000;
  let api: URL;
  try { api = new URL(config.apiBaseUrl); } catch { throw new Error('Pilot setup unavailable'); }
  if (api.origin !== 'http://127.0.0.1:3192' || api.pathname !== '/' || api.search || api.hash || api.username || api.password ||
      !objectId(config.workspaceId) || !objectId(config.ownerUserId) || !token(config.accessToken) || config.search || config.hash ||
      !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000) throw new Error('Pilot setup unavailable');
  const fetcher = config.fetcher ?? fetch;
  let active: AbortController | undefined, uncertain = false, cachedWorkspace: OwnedWorkspace | undefined, cachedBinding: ExecutionBinding | undefined;
  const request = async (path: string, init: RequestInit): Promise<unknown> => {
    const controller = new AbortController(); active?.abort(); active = controller;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetcher(`${api.origin}${path}`, { ...init, credentials: 'include', redirect: 'error', signal: controller.signal, headers: { Authorization: `Bearer ${config.accessToken}`, 'Content-Type': 'application/json' } });
      if (response.status === 401 || response.status === 403) throw new Error('Sign in as the workspace owner');
      if (!response.ok) throw new Error('Pilot setup unavailable');
      return await boundedJson(response);
    } catch (error: unknown) {
      if (error instanceof Error && (error.message === 'Sign in as the workspace owner' || error.message === 'Pilot setup unavailable')) throw error;
      throw new Error('Pilot request timed out or was cancelled');
    } finally { clearTimeout(timer); if (active === controller) active = undefined; controller.abort(); }
  };
  const verify = async (customerId: string) => bindingDto(await request(`/assistant-execution-bindings/${customerId}`, { method: 'GET' }), customerId, config.workspaceId, config.ownerUserId);
  return {
    cancel: () => active?.abort(),
    async load(): Promise<PilotState> {
      const workspace = workspaceDto(await request(`/workspaces/${config.workspaceId}`, { method: 'GET' }), config.workspaceId, config.ownerUserId);
      const customers = await request(`/assistant-customers?workspaceId=${config.workspaceId}`, { method: 'GET' });
      if (!Array.isArray(customers) || customers.length > 1) throw new Error('Pilot setup unavailable');
      cachedWorkspace = workspace;
      cachedBinding = customers.length === 1 ? await verify(customerIdFrom(customers[0], config.workspaceId, config.ownerUserId)) : undefined;
      uncertain = false;
      return { workspace, ...(cachedBinding ? { attachment: cachedBinding } : {}) };
    },
    async attach(): Promise<ExecutionBinding> {
      if (cachedBinding) return cachedBinding;
      if (!cachedWorkspace) throw new Error('Pilot setup unavailable');
      if (uncertain) throw new Error('Attachment status uncertain; recheck');
      uncertain = true;
      try {
        const created = await request('/assistant-customers', { method: 'POST', body: JSON.stringify({ workspaceId: config.workspaceId }) });
        const verified = await verify(customerIdFrom(created, config.workspaceId, config.ownerUserId));
        cachedBinding = verified; uncertain = false; return verified;
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'Sign in as the workspace owner') throw error;
        throw new Error('Attachment status uncertain; recheck');
      }
    },
  };
}
